import Discount from 'src/models/discount/Discount'
import MembershipTier from 'src/models/membership-type/MembershipTier'
import MembershipType from 'src/models/membership-type/MembershipType'
import Model, { encoders, ID, Integer, JsonObject, MODEL_LEVEL } from 'src/models/model'
import Site from 'src/models/site/Site'

export const MEMBERSHIP_DISCOUNT_RENEWAL_TERMS = {
  DAYS: 'days',
  WEEKS: 'weeks',
  MONTHS: 'months',
  YEARS: 'years'
}
MEMBERSHIP_DISCOUNT_RENEWAL_TERMS.ALL = _.values(MEMBERSHIP_DISCOUNT_RENEWAL_TERMS)

export const MEMBERSHIP_DISCOUNT_CONDITIONS = {
  BIRTHDAY: 'birthday',
  BIRTHWEEK: 'birthweek',
  DATE_RANGE: 'date-range',
  DAY_OF_WEEK: 'day-of-week',
  LIFETIME: 'lifetime',
  LONGEVITY: 'longevity',
  SITES: 'sites',
  TIER: 'tier'
}
MEMBERSHIP_DISCOUNT_CONDITIONS.ALL = _.values(MEMBERSHIP_DISCOUNT_CONDITIONS)

export const CONDITION_REQUIREMENTS = {
  // DATE_RANGE
  DATE_RANGE_START: 'dateRangeStart',
  DATE_RANGE_END: 'dateRangeEnd',
  EXCLUDE_DATE_RANGE: 'excludeDateRange',
  // DAY_OF_WEEK
  DAY_OF_WEEK: 'dayOfWeek',
  EXCLUDE_DAY_OF_WEEK: 'excludeDayOfWeek',
  // LIFETIME
  LIFETIME_MINIMUM: 'lifetimeMinimum',
  LIFETIME_MAXIMUM: 'lifetimeMaximum',
  LIFETIME_TERM: 'lifetimeTerm',
  // LONGEVITY
  LONGEVITY_MINIMUM: 'longevityMinimum',
  LONGEVITY_MAXIMUM: 'longevityMaximum',
  LONGEVITY_TERM: 'longevityTerm',
  // SITES
  SITES: 'siteIds',
  EXCLUDE_SITES: 'excludeSites',
  // TIER
  TIER: 'membershipTierIds'
}
CONDITION_REQUIREMENTS.ALL = _.values(CONDITION_REQUIREMENTS)

export default class MembershipDiscount extends Model {
  static name = 'MembershipDiscount'

  static deletable = true
  static versioned = true

  static level = MODEL_LEVEL.CIRCUIT

  static employeeRequiredPermissionCategories = [Model.permissionCategories.Site]

  static define () {
    this.fields = {
      id: {
        type: ID
      },
      name: {
        type: String,
        sortable: true
      },
      membershipType: {
        type: MembershipType,
        encode: encoders.hasOne,
        reference: true,
        output: true
      },
      discount: {
        type: Discount,
        default: null,
        encode: encoders.hasOne,
        reference: true
      },
      limitRedemptions: {
        type: Boolean,
        default: false
      },
      redemptionsLimit: {
        type: Integer,
        default: 1
      },
      renews: {
        type: Integer,
        default: 1
      },
      renewalTerm: {
        type: String,
        default: MEMBERSHIP_DISCOUNT_RENEWAL_TERMS.MONTHS
      },
      membershipTiers: {
        type: [MembershipTier],
        output: true
      },
      membershipTierIds: {
        type: [ID],
        default: () => ([])
      },
      sites: {
        type: [Site],
        reference: true
      },
      conditions: {
        type: JsonObject,
        default: _(MEMBERSHIP_DISCOUNT_CONDITIONS.ALL).keyBy().mapValues(() => false).value()
      },
      conditionsRequirements: {
        type: JsonObject,
        mergeDefault: true,
        default: () => ({
          dayOfWeek: [],
          excludeDayOfWeek: false,
          lifetimeMinimum: 0,
          lifetimeMaximum: 1,
          lifetimeTerm: 'months',
          longevityMinimum: 1,
          longevityMaximum: null,
          longevityTerm: 'months',
          customStartDate: null,
          customEndDate: null,
          excludeDateRange: false,
          excludeSites: false
        })
      },
      enable: {
        type: Boolean,
        default: true
      }
    }

    this.computed = {
      isConditional () {
        return _(this.conditions).values().some(isEnabled => isEnabled)
      },
      formattedConditions () {
        const conditions = _.map(this.$mh.objectToArray(this.conditions), condition => _.startCase(condition))

        if (this.conditions[MEMBERSHIP_DISCOUNT_CONDITIONS.TIER]) {
          const tierNames = _(this.membershipTiers).map('name').compact().value()
          if (tierNames.length) {
            _.remove(conditions, condition => condition === _.startCase(MEMBERSHIP_DISCOUNT_CONDITIONS.TIER))
            conditions.push(tierNames.join(', '))
          }
        }
        if (this.conditions[MEMBERSHIP_DISCOUNT_CONDITIONS.DAY_OF_WEEK]) {
          _.remove(conditions, condition => condition === _.startCase(MEMBERSHIP_DISCOUNT_CONDITIONS.DAY_OF_WEEK))
          conditions.push(`${this.conditionsRequirements.excludeDayOfWeek ? 'Exclude' : 'Include'} ${_.map(this.conditionsRequirements.dayOfWeek, index => ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][index]).join(', ')}`)
        }
        if (this.conditions[MEMBERSHIP_DISCOUNT_CONDITIONS.LIFETIME]) {
          _.remove(conditions, condition => condition === _.startCase(MEMBERSHIP_DISCOUNT_CONDITIONS.LIFETIME))
          if (this.conditionsRequirements.lifetimeMaximum) {
            conditions.push(`${this.conditionsRequirements.lifetimeMinimum}-${this.conditionsRequirements.lifetimeMaximum} ${_.startCase(this.conditionsRequirements.lifetimeTerm).slice(0, -1)} Member`)
          } else {
            conditions.push(`${this.conditionsRequirements.lifetimeMinimum}+ ${_.startCase(this.conditionsRequirements.lifetimeTerm).slice(0, -1)} Member`)
          }
        }
        if (this.conditions[MEMBERSHIP_DISCOUNT_CONDITIONS.LONGEVITY]) {
          _.remove(conditions, condition => condition === _.startCase(MEMBERSHIP_DISCOUNT_CONDITIONS.LONGEVITY))
          if (this.conditionsRequirements.longevityMaximum) {
            conditions.push(`${this.conditionsRequirements.longevityMinimum}-${this.conditionsRequirements.longevityMaximum} ${_.startCase(this.conditionsRequirements.longevityTerm).slice(0, -1)} Member`)
          } else {
            conditions.push(`${this.conditionsRequirements.longevityMinimum}+ ${_.startCase(this.conditionsRequirements.longevityTerm).slice(0, -1)} Member`)
          }
        }
        if (this.conditions[MEMBERSHIP_DISCOUNT_CONDITIONS.DATE_RANGE]) {
          _.remove(conditions, condition => condition === MEMBERSHIP_DISCOUNT_CONDITIONS.DATE_RANGE)
          let customDateCondition = `${this.i18n.d(new Date(this.conditionsRequirements.dateRangeStart), 'shortDate')}`
          if (this.conditionsRequirements.dateRangeEnd) {
            customDateCondition += ` to ${this.i18n.d(new Date(this.conditionsRequirements.dateRangeEnd), 'shortDate')}`
          } else {
            customDateCondition += '+'
          }
          conditions.push(customDateCondition)
        }

        return conditions
      }
    }
  }
}
